/**
 * MoneyFlowByMethodCard — end-of-day breakdown of today's counter LOADS
 * AND CASHOUTS by payment method. Two stacked sections so the cashier can
 * reconcile both directions of the till in one glance. Kept the file name
 * (LoadsByMethodCard.js) so the import in CashierDashboard.js stays stable.
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Banknote, ArrowUpRight, ArrowDownLeft, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHOD_TONE = {
    'Cash':         'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
    'Cash App':     'border-sky-500/40 bg-sky-500/10 text-sky-200',
    'Chime':        'border-lime-500/40 bg-lime-500/10 text-lime-200',
    'PayPal':       'border-blue-500/40 bg-blue-500/10 text-blue-200',
    'Venmo':        'border-indigo-500/40 bg-indigo-500/10 text-indigo-200',
    'Zelle':        'border-purple-500/40 bg-purple-500/10 text-purple-200',
    'Apple Pay':    'border-zinc-300/30 bg-zinc-200/10 text-zinc-100',
    'Square':       'border-slate-400/40 bg-slate-400/10 text-slate-200',
    'Other':        'border-zinc-700 bg-zinc-900 text-zinc-200',
    'Legacy':       'border-stone-600/40 bg-stone-700/10 text-stone-300',
    'Unspecified':  'border-rose-500/40 bg-rose-500/10 text-rose-200',
};

const Section = ({ title, icon: Icon, accent, data, testId, onTagClick }) => {
    if (!data) return null;
    const isEmpty = !data.methods || data.methods.length === 0;
    return (
        <div className="card-dark p-4" data-testid={testId}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${accent}`} />
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{title}</p>
                </div>
                <div className="text-right">
                    <p className="text-xl font-mono font-bold text-white">{fmtMoney(data.grand_total)}</p>
                    <p className="text-[10px] text-zinc-500">{data.grand_count} entries · CT today</p>
                </div>
            </div>
            {isEmpty ? (
                <p className="text-xs text-zinc-500 text-center py-3">
                    Nothing logged yet today.
                </p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {data.methods.map(m => {
                        const tone = METHOD_TONE[m.method] || METHOD_TONE['Other'];
                        const pct = data.grand_total > 0 ? (m.total / data.grand_total) * 100 : 0;
                        const clickable = m.method === 'Unspecified' && typeof onTagClick === 'function';
                        const tile = (
                            <>
                                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80 flex items-center justify-between">
                                    <span>{m.method}</span>
                                    {clickable && (
                                        <span className="text-[9px] font-normal opacity-90 underline">Tag →</span>
                                    )}
                                </p>
                                <p className="text-lg font-mono font-bold mt-1">{fmtMoney(m.total)}</p>
                                <p className="text-[10px] opacity-70">{m.count} · {pct.toFixed(0)}%</p>
                            </>
                        );
                        return clickable ? (
                            <button key={m.method} type="button"
                                onClick={onTagClick}
                                className={`rounded-md border ${tone} p-2.5 text-left hover:brightness-125 transition cursor-pointer`}
                                data-testid={`${testId}-tile-${m.method.replace(/\s+/g, '-').toLowerCase()}-tag-btn`}>
                                {tile}
                            </button>
                        ) : (
                            <div key={m.method} className={`rounded-md border ${tone} p-2.5`}
                                data-testid={`${testId}-tile-${m.method.replace(/\s+/g, '-').toLowerCase()}`}>
                                {tile}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const MoneyFlowByMethodCard = ({
    loadsEndpoint = '/cashier/loads-by-method-today',
    cashoutsEndpoint = '/cashier/cashouts-by-method-today',
    loadsTitle = "Today's Loads by Method",
    cashoutsTitle = "Today's Cashouts by Method",
    onTagLoadsClick,
    onTagCashoutsClick,
    refreshKey = 0,
}) => {
    const [loads, setLoads] = useState(null);
    const [cashouts, setCashouts] = useState(null);
    const [loading, setLoading] = useState(true);
    const timerRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            try {
                const [a, b] = await Promise.all([
                    axios.get(`${API}${loadsEndpoint}`, { withCredentials: true }),
                    axios.get(`${API}${cashoutsEndpoint}`, { withCredentials: true }),
                ]);
                if (mounted) { setLoads(a.data); setCashouts(b.data); }
            } catch { /* silent */ }
            finally { if (mounted) setLoading(false); }
        };
        fetch();
        timerRef.current = setInterval(fetch, 30_000);
        return () => { mounted = false; clearInterval(timerRef.current); };
    }, [loadsEndpoint, cashoutsEndpoint, refreshKey]);

    if (loading) {
        return (
            <div className="card-dark p-4 text-zinc-500 text-xs flex items-center gap-2"
                data-testid="money-flow-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading today&apos;s flow…
            </div>
        );
    }

    const net = (loads?.grand_total || 0) - (cashouts?.grand_total || 0);
    const hasAny = (loads?.grand_total || 0) > 0 || (cashouts?.grand_total || 0) > 0;

    return (
        <div className="space-y-3" data-testid="money-flow-by-method">
            <Section title={loadsTitle} icon={ArrowDownLeft} accent="text-emerald-400"
                data={loads} testId="loads-by-method-card" onTagClick={onTagLoadsClick} />
            <Section title={cashoutsTitle} icon={ArrowUpRight} accent="text-rose-400"
                data={cashouts} testId="cashouts-by-method-card" onTagClick={onTagCashoutsClick} />
            {hasAny && (
                <div className="card-dark p-3 flex items-center gap-2 text-xs">
                    <Banknote className="w-3.5 h-3.5 text-amber-300" />
                    <span className="text-zinc-400">Net today:</span>
                    <span className={`font-mono font-bold ${net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}
                        data-testid="money-flow-net">
                        {fmtMoney(net)}
                    </span>
                    <span className="text-zinc-600 text-[10px]">(loads − cashouts)</span>
                </div>
            )}
        </div>
    );
};

export default MoneyFlowByMethodCard;
